import { GridApiConfig, GridEnvironment } from '../api/grid-api-client';
import {  TransactionPayload, TransactionResult } from '../managers/signing-manager';



/////////// Providers ///////////

/** @hidden */
export interface ProviderConfig {
  apiKey?: string;
  apiUrl?: string;
  timeout?: number;
  retryAttempts?: number;
  metadata?: Record<string, unknown>;
  environment?: string; // Auto-injected from factory function
}

/** @hidden */
export type AuthProvider = 'turnkey' | 'passkey' | 'solana' | 'privy';


/////////// Errors ///////////

/** @hidden */
export interface ErrorDetail {
  field?: string;
  code: string;
  message: string;
  suggestion: string;
  documentation: string;
}

/**
 * Base error class for SDK operations with consistent error structure
 * @hidden
 */
export class GridError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly statusCode?: number,
    public readonly details?: ErrorDetail[] | any,
    public readonly timestamp?: string,
    public readonly lastResponse?: any
  ) {
    super(message);
    this.name = 'GridError';
  }

  /** Format error with suggestions and documentation */
  getFormattedMessage(): string {
    let formatted = this.message;

    if (this.details && Array.isArray(this.details) && this.details.length > 0) {
      const suggestions = this.details
        .filter(detail => detail.suggestion && detail.suggestion.trim())
        .map(detail => detail.suggestion);

      if (suggestions.length > 0) {
        formatted += `\n\nSuggestions:\n${suggestions.map(s => `• ${s}`).join('\n')}`;
      }

      const docs = this.details
        .filter(detail => detail.documentation && detail.documentation.trim())
        .map(detail => detail.documentation);

      if (docs.length > 0) {
        formatted += `\n\nDocumentation: ${docs[0]}`;
      }
    }

    return formatted;
  }

  /** Format error for detailed console output */
  toString(): string {
    let output = `${this.name}: ${this.message}`;

    if (this.lastResponse?.requestId) {
      output += `\n  Request ID: ${this.lastResponse.requestId}`;
    }

    if (this.timestamp) {
      output += `\n  Timestamp: ${this.timestamp}`;
    }

    if (this.code) {
      output += `\n  Error Code: ${this.code}`;
    }

    if (this.statusCode) {
      output += `\n  Status Code: ${this.statusCode}`;
    }

    if (this.details && Array.isArray(this.details) && this.details.length > 0) {
      output += `\n  Details:`;
      this.details.forEach(detail => {
        if (detail.field) {
          output += `\n    - Field: ${detail.field}`;
        }
        if (detail.message) {
          output += `\n      Message: ${detail.message}`;
        }
        if (detail.suggestion) {
          output += `\n      Suggestion: ${detail.suggestion}`;
        }
      });
    }

    return output;
  }
}

/**
 * @hidden
 */
export class AuthorizationError extends GridError {
  constructor(
    message: string,
    code: string,
    public readonly provider?: AuthProvider,
    public readonly cause?: Error,
    details?: ErrorDetail[],
    timestamp?: string,
    requestId?: string
  ) {
    super(message, code, undefined, details, timestamp, { requestId, provider, cause });
    this.name = 'AuthorizationError';
  }

  // Override toString to include provider info
  toString(): string {
    let output = super.toString();

    if (this.provider) {
      output += `\n  Provider: ${this.provider}`;
    }

    if (this.cause) {
      output += `\n  Caused by: ${this.cause.message}`;
    }

    return output;
  }
}

/**
 * @hidden
 */
export class ProviderError extends GridError {
  constructor(
    message: string,
    public readonly provider: AuthProvider,
    statusCode?: number,
    public readonly cause?: Error,
    details?: ErrorDetail[],
    timestamp?: string,
    requestId?: string
  ) {
    super(message, 'PROVIDER_ERROR', statusCode, details, timestamp, { requestId, provider, cause });
    this.name = 'ProviderError';
  }

  // Override toString to include provider and cause info
  toString(): string {
    let output = super.toString();

    if (this.provider) {
      output += `\n  Provider: ${this.provider}`;
    }

    if (this.cause) {
      output += `\n  Caused by: ${this.cause.message}`;
    }

    return output;
  }
}

/////////// Key Management ///////////

/** Key pair for cryptographic operations (publicKeyUncompressed required for Turnkey signing) */
export interface UniversalKeyPair {
  publicKey: string;
  privateKey: string;
  publicKeyUncompressed?: string;
}

/** @hidden */
export type KeyPairTag = 'primary' | 'backup' | 'solana' | 'passkey';

/** @hidden */
export interface TaggedKeyPair extends UniversalKeyPair {
  provider: SignerProvider;
  tag: KeyPairTag;
}

/** @hidden */
export type SessionSecrets = TaggedKeyPair[];

/////////// Types / Enums ///////////

/** @hidden */
export type Env = 'devnet' | 'mainnet' | 'testnet';


/////////// Authentication ///////////

// Init Auth Request
export interface InitAuthRequest {
  email?: string;
  expiration?: number;
  smartAccountAddress?: string;
}

/** Authentication initialization data for MPC providers (Privy, Turnkey, etc.) */
export interface InitAuthData {
  type: string;
  email: string;
  status: string;
  /** Whether OTP was sent successfully */
  otpSent: boolean;
  otpId?: string;
  /** MPC provider type */
  provider: 'privy' | 'dynamic' | 'passkey' | 'turnkey' | 'external';
  /** Creation timestamp (ISO string) */
  createdAt: string;
  /** Expiration timestamp (ISO string) */
  expiresAt: string;
}

/** Authentication initialization data for EOA/Solana wallets */
export interface InitAuthEOAData {
  authenticated: boolean;
  /** Provider type (typically 'solana') */
  provider: string;
  /** Wallet public key */
  publicKey: string;
}

/**
 * API response metadata for request tracking and debugging
 *
 * This provides metadata
 * about the API request for troubleshooting and support queries.
 *
 * @category API
 *
 * @remarks
 * Use this metadata to:
 * - Track requests by requestId when contacting support
 * - Verify successful requests via statusCode
 * - Implement retry logic based on idempotencyKey
 * - Debug issues by inspecting response headers
 *
 * @example Logging request details
 * ```typescript
 * const response = await gridClient.getAccount(address);
 *
 * console.log('Request ID:', response.lastResponse?.requestId);
 * console.log('Status:', response.lastResponse?.statusCode);
 * console.log('All headers:', response.lastResponse?.headers);
 * ```
 */
export interface LastResponse {
  /**
   * Unique identifier from x-request-id header
   *
   * Use this when contacting support to help identify the specific request.
   */
  requestId: string;

  /**
   * HTTP status code (200, 201, 400, etc.)
   *
   * Indicates the success status of the request.
   */
  statusCode: number;

  /**
   * Idempotency key from x-idempotency-key header
   *
   * Used to safely retry requests without duplicate effects.
   * Only present if the request included an idempotency key.
   */
  idempotencyKey?: string;

  /**
   * All response headers as key-value pairs
   *
   * Contains all HTTP headers returned by the server.
   */
  headers: Record<string, string>;
}

/**
 * Base interface for all API responses with data and metadata
 *
 * All successful API responses follow this wrapper pattern for consistency and debugging.
 * Errors throw GridError instead of returning error objects.
 *
 * @template T - Type of response data
 * @category API
 *
 * @remarks
 * The BaseResponse wrapper provides:
 * - **data**: The actual response payload from the API
 * - **lastResponse**: Request metadata for debugging and tracking
 *
 * This pattern making it easy to:
 * - Access response data via the `data` property
 * - Track requests using `lastResponse.requestId` for support queries
 * - Debug issues with status codes and headers
 * - Implement idempotency using `lastResponse.idempotencyKey`
 *
 * Note: Errors are NOT returned in the response. Instead, they throw GridError with similar metadata.
 *
 * @example Accessing response data
 * ```typescript
 * const response = await gridClient.getAccount(accountAddress);
 *
 * console.log(response.data.address);
 * console.log(response.data.policies);
 * ```
 *
 * @example Accessing request metadata for debugging
 * ```typescript
 * const response = await gridClient.createAccount({ email: 'user@example.com' });
 *
 * console.log('Request ID:', response.lastResponse?.requestId);
 * console.log('Status:', response.lastResponse?.statusCode);
 * console.log('Idempotency Key:', response.lastResponse?.idempotencyKey);
 * ```
 *
 * @example Error handling
 * ```typescript
 * try {
 *   const response = await gridClient.getAccount(accountAddress);
 *   console.log('Success:', response.data);
 * } catch (error) {
 *   if (error instanceof GridError) {
 *     console.error('Error:', error.message);
 *     console.error('Request ID:', error.lastResponse?.requestId);
 *   }
 * }
 * ```
 */
export interface BaseResponse<T = any> {
  /**
   * Response data from API
   *
   * Contains the actual payload returned by the API endpoint.
   * The type varies based on the specific response interface.
   */
  data: T;

  /**
   * Response metadata with request ID and status code
   *
   * Useful for debugging and tracking requests. Contains:
   * - requestId: Unique identifier for this request
   * - statusCode: HTTP status code (200, 201, etc.)
   * - headers: All response headers
   * - idempotencyKey: Key for idempotent requests (if applicable)
   *
   * @see LastResponse
   */
  lastResponse?: LastResponse;
}

/** Authentication initialization response (data varies by provider type) */
export interface InitAuthResponse extends BaseResponse<InitAuthData | InitAuthEOAData> {}

/** @hidden */
export interface CompleteAuthRequest {
  expiration?: number;
}

/** Complete authentication request with OTP verification */
export interface CompleteAuthRequestWithOtp extends CompleteAuthRequest {
  /** OTP code received by user */
  otpCode: string;
  user: GridClientUserContext;
  /** Session-specific cryptographic secrets */
  sessionSecrets: SessionSecrets;
  /** Optional smart account address to scope authentication to a specific account */
  smartAccountAddress?: string;
}

// Complete Auth Response
export interface CompleteAuthResponse extends BaseResponse<{
  address: string;
  policies: AccountPolicies;
  gridUserId: string;
  authentication: AuthenticationData;
}> {}

// Complete Auth And Create Account Request
export interface CompleteAuthAndCreateAccountRequest {
  otpCode: string;
  otpId?: string;
  provider?: 'privy' | 'turnkey';
  user: GridClientUserContext;
  sessionSecrets: SessionSecrets;
}

/** @hidden */
export interface SignRequest {
  transaction: string;
  sessionSecrets: SessionSecrets;
  // session?: any; // Generic session type, can be PrivySessionData or other provider sessions
}

/** Transaction submission request */
export interface SendTransactionRequest {
  /** Signed transaction payload */
  signedTransactionPayload: TransactionResult;
  /** Target address */
  address: string;
}


// Grid-specific payment intent types
/** @hidden */
export interface ConfirmPayload {
  intentPayload: string;
  mpcPayload: string;
}

/** Spending limit period */
export type SpendingLimitPeriod = 'one_time' | 'daily' | 'weekly' | 'monthly';

/** Create spending limit request */
export interface SpendingLimitRequest {
  /** Amount (min: 1) */
  amount: string | number;
  /** Token mint address */
  mint: string;
  /** Spending limit period */
  period: SpendingLimitPeriod;
  /** Signers required to approve transaction (account addresses) */
  transactionSigners?: string[];
  /** Signers authorized to use this limit (account addresses) */
  spendingLimitSigners: string[];
  /** Allowed destination addresses (optional) */
  destinations?: string[];
  /** Unix timestamp expiration (optional) */
  expiration?: number;
}


/** Transaction submission response */
export interface TransactionSubmissionResponse extends BaseResponse<{
  /** Transaction signature */
  transactionSignature: string;
  /** Confirmation timestamp */
  confirmedAt: string;
}> {}

/** Passkey permission */
export type PasskeyPermission = string;

/** Passkey information */
export interface PasskeyInfo {
  address: string;
  role: string;
  permissions: PasskeyPermission[];
  provider: string;
  /** ISO timestamp when passkey was added */
  addedAt: string;
}

/** Get passkeys response */
export interface GetPasskeysResponse extends BaseResponse<{
  accountAddress: string;
  passkey: PasskeyInfo | null;
}> {}

/** Input data for adding a passkey signer */
export interface PasskeyInput {
  /** Passkey public key address */
  address: string;
  /** Signer role (defaults to 'primary') */
  role?: string;
  /** Permissions granted to passkey (defaults to all permissions) */
  permissions?: PasskeyPermission[];
}

/** Request to add passkey signer to account */
export interface AddPasskeyRequest {
  /** Passkey configuration */
  passkey: PasskeyInput;
  /** Required transaction signers (account addresses) */
  transactionSigners?: string[];
}

/** Query parameters for adding passkey */
export interface AddPasskeyQueryParams {
  /** Whether to add as admin signer (defaults to false) */
  admin?: boolean;
}

/** Add passkey response */
export interface AddPasskeyResponse extends BaseResponse<{
  transaction: string;
  passkey: PasskeyInfo;
  threshold: number;
  status: string;
  /** ISO timestamp when transaction expires */
  expiresAt: string;
}> {}

/** Signer role in multi-signature account */
export type SignerRole = 'primary' | 'backup';

/**
 * Signer permissions in multi-signature account
 * - CAN_INITIATE: Initiate transactions
 * - CAN_EXECUTE: Execute approved transactions
 * - CAN_VOTE: Vote on pending transactions
 */
export type SignerPermission = 'CAN_INITIATE' | 'CAN_EXECUTE' | 'CAN_VOTE';

/**
 * Signer in multi-signature account
 *
 * Note: role defaults to 'primary', provider defaults to 'external'
 *
 * @example
 * ```typescript
 * const signer: AccountSigner = {
 *   address: '5FHwkrdxntdK24hgQU8qgBjn35Y1zwhz1GZwCkP2UJnM',
 *   permissions: ['CAN_INITIATE', 'CAN_EXECUTE', 'CAN_VOTE']
 * };
 * const backupSigner: AccountSigner = {
 *   address: '...',
 *   role: 'backup',
 *   permissions: ['CAN_VOTE'],
 *   provider: 'turnkey'
 * };
 * ```
 */
export interface AccountSigner {
  /** Public key address */
  address: string;
  /** Role (defaults to 'primary') */
  role?: SignerRole;
  /** Permissions granted to signer */
  permissions: SignerPermission[];
  /** Authentication provider (defaults to 'external') */
  provider?: string;
}

/**
 * Update account configuration (all fields optional)
 *
 * @example
 * ```typescript
 * const update: UpdateAccountRequest = {
 *   threshold: 2,
 *   transactionSigners: ['5FHwkrdxntdK24hgQU8qgBjn35Y1zwhz1GZwCkP2UJnM']
 * };
 * const withSigners: UpdateAccountRequest = {
 *   signers: [{
 *     address: 'newSignerPublicKey',
 *     role: 'backup',
 *     permissions: ['CAN_VOTE']
 *   }],
 *   threshold: 2
 * };
 * ```
 */
export interface UpdateAccountRequest {
  /** Signers (replaces existing if provided) */
  signers?: AccountSigner[];
  /** Approval threshold (must not exceed signers.length, range: 1-10) */
  threshold?: number;
  /** Time lock in seconds (range: 1 to u32::MAX) */
  timeLock?: number;
  /** Admin address (pass null to remove) */
  adminAddress?: string | null;
  /** Signer addresses that must approve this update */
  transactionSigners?: string[];
}

/**
 * Simplified account creation input (provide email or signer)
 *
 * @example
 * ```typescript
 * const emailAccount: CreateAccountInput = { email: 'user@example.com' };
 * const signerAccount: CreateAccountInput = { signer: 'publicKeyString' };
 * ```
 */
export interface CreateAccountInput {
  /** Email for email-based account */
  email?: string;
  /** Public key for signer-based account */
  signer?: string;
}

/**
 * Full account creation request (discriminated union)
 *
 * Note: For simple creation, use {@link CreateAccountInput}. This type is for advanced use cases with full policy control.
 *
 * @see {@link EmailAccountRequest} for email accounts
 * @see {@link SignersAccountRequest} for signer accounts
 * @see {@link AccountPolicies} for direct policy configuration
 */
export type CreateAccountRequest = EmailAccountRequest | SignersAccountRequest | AccountPolicies;

/** Email-based account creation request */
export interface EmailAccountRequest {
  type: 'email';
  email: string;
}

/** Signer-based account creation request with full policy control */
export interface SignersAccountRequest {
  type: 'signers';
  /** Account policies (signers, threshold, restrictions) */
  policies: AccountPolicies;
  memo?: string;
}

/**
 * Multi-signature account policies
 *
 * Important: Signers range: 1-10, threshold must not exceed signers.length (range: 1-10)
 */
export interface AccountPolicies {
  /** Account signers (range: 1-10) */
  signers: AccountSigner[];
  /** Required signatures (must not exceed signers.length) */
  threshold: number;
  /** Time lock in seconds (range: 1 to u32::MAX) */
  timeLock?: number | null;
  /** Admin address for administrative functions */
  adminAddress?: string | null;
}

/**
 * @deprecated Use {@link EmailAccountRequest} instead
 */
export interface EmailPayload {
  email: string;
}

/**
 * @deprecated Use {@link SignersAccountRequest} instead
 */
export interface SignersPayload {
  policies: AccountPolicies;
}

/**
 * Email account creation response
 *
 * Note: Requires authentication via initAuth and completeAuth before use
 * @hidden
 */
export interface EmailAccountResponse extends BaseResponse<{
  type: 'email';
  email: string;
  status: string;
  otpSent: boolean;
  createdAt: string;
  expiresAt: string;
  memo?: string;
  address?: string;
  policies?: AccountPolicies;
  gridUserId?: string;
}> {}

/**
 * Signer account creation response (immediately active)
 * @hidden
 */
export interface SignersAccountResponse extends BaseResponse<{
  type: 'signers';
  address: string;
  policies: AccountPolicies;
  gridUserId: string;
  /** Optional memo for the account */
  memo?: string;
  /** Email address (only present for email accounts) */
  email?: string;
  /** Current status (only present for email accounts) */
  status?: string;
  /** Whether an OTP code was sent (only present for email accounts) */
  otpSent?: boolean;
  /** ISO timestamp when created (only present for email accounts) */
  createdAt?: string;
  /** ISO timestamp when expires (only present for email accounts) */
  expiresAt?: string;
}> {}

/**
 * Account creation response (union type)
 *
 * @see {@link EmailAccountResponse} for email accounts
 * @see {@link SignersAccountResponse} for signer accounts
 */
export type CreateAccountResponse = EmailAccountResponse | SignersAccountResponse;

/** Get account response */
export interface GetAccountResponse extends BaseResponse<{
    type: 'signers' | 'email';
    address: string;
    /** Policies (optional for email accounts) */
    policies?: AccountPolicies;
    gridUserId: string;
    memo?: string;
    createdAt?: string;
    /** Status (for email accounts) */
    status?: string;
}> {}

/** Transaction in account history */
export interface AccountTransaction {
  id: string;
  /** Transaction signature hash */
  signature: string;
  status: string;
  /** ISO timestamp when created */
  createdAt: string;
  /** ISO timestamp when confirmed */
  confirmedAt?: string;
}

/** Get transactions response */
export interface GetTransactionsResponse extends BaseResponse<{
  transactions?: AccountTransaction[];
  error?: string;
}> {}

// Get Account Balances types
/** @hidden */
export interface TokenBalance {
  tokenAddress: string;
  amount: bigint;
  decimals: number;
  amountDecimal: string;
  symbol?: string;
  name?: string;
  logo?: string;
}

/** Query parameters for paginated balance requests */
export interface GetAccountBalancesQueryParams {
  /** Results per page (default: 10) */
  limit?: number;
  /** Page number (default: 1) */
  page?: number;
}

/** Account balances data with SOL and token balances */
export interface AccountBalancesData {
  address: string;
  lamports: bigint;
  sol: number;
  tokens: TokenBalance[];
}

export interface AccountBalancesResponse extends BaseResponse<AccountBalancesData> {
  data: AccountBalancesData;
}


/** Payment details for transfers */
export interface PaymentDetails {
  /** Account (for source) */
  account?: string;
  /** Address (for crypto destination) */
  address?: string;
  currency: string;
  /** Payment rail (for fiat destination, e.g., "ach_push") */
  paymentRail?: string;
  /** External account ID (for fiat destination) */
  externalAccountId?: string;
}

/** Create payment intent request */
export interface CreatePaymentIntentRequest {
  /** Transfer amount as a string (e.g., "100.00") */
  amount: string;
  /** Source account details */
  source: PaymentDetails;
  /** Destination account details */
  destination: PaymentDetails;
  /** Fee configuration. Required for non-Enterprise customers. Enterprise customers may omit this as fees are handled by their plan. */
  feeConfig?: FeeConfig;
}

// Raw API response from server (matches Rust struct)
/** @hidden */
export type CreatePaymentIntentApiResponse = any;

// Client response (follows standard data pattern)
export interface CreatePaymentIntentResponse extends BaseResponse<{
  transactionPayload?: TransactionPayload;
  error?: string;
} & Omit<CreatePaymentIntentApiResponse, 'kms_payloads' | 'transaction_signers' | 'transaction'>> {}

/** KMS payload for transaction signing */
export interface KmsPayload {
  /** KMS provider name (e.g., 'turnkey', 'privy') */
  provider: string;
  /** Signer's public key address */
  address: string;
  /** Encrypted signing payload */
  payload: string;
}

/** Update spending limit request */
export interface UpdateSpendingLimitRequest {
  /** Amount (min: 1) */
  amount?: string | number;
  mint?: string;
  period?: SpendingLimitPeriod;
  spendingLimitSigners?: string[];
  destinations?: string[];
  expiration?: number;
  transactionSigners?: string[];
}

/** Use spending limit request (consume from existing limit) */
export interface UseSpendingLimitRequest {
  /** Amount to use from limit (min: 1) */
  amount: string | number;
  /** Signer address authorized to use limit */
  signerAddress: string;
  /** Recipient address for transfer */
  recipientAddress: string;
}

/** Create spending limit response */
export interface CreateSpendingLimitResponse extends BaseResponse<{
  /** On-chain spending limit address */
  spendingLimitAddress: string;
  /** Serialized transaction for signing */
  transaction: string;
  /** KMS payloads for multi-party signing */
  kmsPayloads: KmsPayload[];
  transactionSigners: string[];
}> {}

/** Spending limit transaction response (for update/delete operations) */
export interface SpendingLimitTransactionResponse extends BaseResponse<{
  /** Serialized transaction for signing */
  transaction: string;
  transactionSigners: string[];
  /** KMS payloads for multi-party signing */
  kmsPayloads: KmsPayload[];
}> {}

/** Spending limit data with configuration, usage, and metadata */
export interface SpendingLimit {
  /** On-chain spending limit address */
  address: string;
  /** Settings account address */
  settingsAddress: string;
  /** Main smart account address */
  mainAccountAddress: string;
  /** Environment (sandbox or production) */
  environment: string;
  /** Token mint address */
  mint: string;
  /** Maximum amount per period */
  amount: number;
  /** Period type (0=one_time, 1=daily, 2=weekly, 3=monthly) */
  period: number;
  /** Remaining amount in current period */
  remainingAmount: number;
  status: string;
  /** Unix timestamp of last reset */
  lastReset: number;
  /** Authorized signer addresses */
  signers: string[];
  /** Allowed destination addresses */
  destinations: string[];
  /** Unix timestamp of expiration (0 for no expiration) */
  expiration: number;
  /** Solana slot of last modification */
  slot: number;
  /** Block timestamp of last modification */
  blockTime: string;
  /** Last modification transaction signature */
  lastModifiedSignature: string;
}

/** Get spending limit response */
export interface SpendingLimitResponse extends BaseResponse<SpendingLimit> {}

/** Get spending limits response */
export interface SpendingLimitsResponse extends BaseResponse<SpendingLimit[]> {}

/** KYC verification type */
export type KycType = 'individual' | 'business';

/** KYC endorsement */
export type KycEndorsement = 'ach' | 'sepa';

/** Request KYC link */
export interface RequestKycLinkRequest {
  type: KycType;
  email: string;
  fullName: string;
  endorsements?: KycEndorsement[];
  redirectUri?: string;
}

export interface KycLinkResponse extends BaseResponse<KycLink> {
  data: KycLink;
}

/** KYC verification link data */
export interface KycLink {
  id: string;
  fullName: string;
  email: string;
  type: KycType;
  /** URL for KYC identity verification */
  kycLink: string;
  /** URL for terms of service acceptance */
  tosLink: string;
  kycStatus: KycStatus;
  /** Rejection reasons if KYC was rejected */
  rejectionReasons: string[];
  tosStatus: TosStatus;
  /** ISO timestamp of creation */
  createdAt: string;
  /** External customer ID in KYC provider system */
  customerId: string;
  /** Persona inquiry type */
  personaInquiryType: string;
}


// Get KYC Status types
/** @hidden */
export type KycStatus = 'pending' | 'approved' | 'rejected' | 'under_review';
/** @hidden */
export type TosStatus = 'pending' | 'approved' | 'rejected';

/** @hidden */
export interface RejectionReason {
  code: string;
  message: string;
}

export interface KycStatusResponse extends BaseResponse<KycStatusData> {
  data: KycStatusData;
}

/** KYC verification status data */
export interface KycStatusData {
  id: string;
  account: string;
  type: KycType;
  status: KycStatus;
  tosStatus: TosStatus;
  /** Link to continue KYC if incomplete */
  kycContinuationLink?: string;
  /** Rejection reasons if rejected */
  rejectionReasons: RejectionReason[];
  /** Outstanding requirements to complete verification */
  requirementsDue: string[];
  /** ISO timestamp of creation */
  createdAt: string;
  /** ISO timestamp of last update */
  updatedAt: string;
}

/** Currency type */
export type Currency = 'sol' | 'usdc' | 'usdt' | 'pyusd' | 'eurc';

/** Fee configuration. Required for non-Enterprise customers when creating payment intents or preparing transactions. */
export interface FeeConfig {
  /** Currency used to pay fees */
  currency: Currency;
  /** Public key address of the account paying fees */
  payerAddress: string;
  /** When true, fees are deducted from the payer account rather than being handled by Grid */
  selfManagedFees?: boolean;
}

/** Prepare arbitrary transaction request */
export interface PrepareArbitraryTransactionRequest {
  transaction: string;
  /** @deprecated Use accountSigners instead */
  transactionSigners?: string[];
  accountSigners?: string[];
  /** @deprecated Use feeConfig instead */
  payerAddress?: string;
  feeConfig?: FeeConfig;
}

/** Create proposal request */
export interface CreateProposalRequest {
  transaction: string;
  signer: string;
  rentPayer?: string;
}

/** Create proposal response */
export interface CreateProposalResponse extends BaseResponse<{
  transactions: string[];
  signer: string;
  rentPayer?: string;
}> {}

/** Vote action types for proposals */
export type VoteAction = 'approve' | 'reject' | 'cancel';

/** Vote on proposal request */
export interface VoteProposalRequest {
  /** Array of signer addresses that will vote (must have CAN_VOTE permission) */
  signers: string[];
  /** Vote action to perform */
  action: VoteAction;
}

/** Vote on proposal response */
export interface VoteProposalResponse extends BaseResponse<{
  /** Base64-encoded unsigned transaction */
  transaction: string;
  /** Signers that need to sign the transaction */
  transactionSigners: string[];
}> {}

/** Execute proposal request */
export interface ExecuteProposalRequest {
  /** Signer address that will execute (must have CAN_EXECUTE permission) */
  signer: string;
}

/** Execute proposal response */
export interface ExecuteProposalResponse extends BaseResponse<{
  /** Base64-encoded unsigned transactions */
  transactions: string[];
  signer: string;
}> {}

/** Query parameters for preparing arbitrary transactions */
export interface PrepareArbitraryTransactionQueryParams {
  /** Enable debug mode (default: false) */
  debug?: boolean;
}

// ============= Smart Transaction Types =============

/**
 * Period types for spending limits and policies
 */
export type Period = 'OneTime' | 'Daily' | 'Weekly' | 'Monthly';

/**
 * Policy expiration arguments
 */
export interface PolicyExpirationArgs {
  /** Number of periods until expiration (e.g., 2 for "2 weeks" if period is Weekly) */
  numPeriods: number;
  /** Period type for expiration calculation */
  period: Period;
}

/**
 * Signer permission types for policy signers
 */
export type PolicyPermission = 'CAN_INITIATE' | 'CAN_VOTE' | 'CAN_EXECUTE';

/**
 * Policy signer configuration
 */
export interface PolicySigner {
  /** Public key address of the signer */
  address: string;
  /** Permissions granted to this signer */
  permissions: PolicyPermission[];
}

/**
 * Spending limit configuration for trade policies
 */
export interface SpendingLimitPolicy {
  /** Maximum amount that can be spent (as string to handle large numbers) */
  amount: string;
  /** Token mint address */
  mint: string;
  /** Period type for the spending limit */
  period: SpendingLimitPeriod;
  /** Optional start timestamp (Unix timestamp) */
  startTimestamp?: number;
  /** Optional expiration timestamp (Unix timestamp) */
  expirationTimestamp?: number;
}

/**
 * Trade instruction union type
 *
 * Currently supports Jupiter V6 swaps, extensible for other programs
 *
 * NOTE: The Rust API uses #[serde(flatten)] so instruction and args are at the same level as program,
 * not nested in a payload object.
 */
export type TradeInstruction = {
  /** Program identifier */
  program: 'jup-v6';
  /** Instruction name (e.g., "route_v2", "exact_out_route_v2") */
  instruction: string;
  /** Instruction arguments as JSON - must match IDL arg names 1:1 */
  args: Record<string, any>;
};

/**
 * Trade policies configuration
 */
export interface TradePolicies {
  /** Array of signers authorized for this policy (minimum 1 required) */
  signers: PolicySigner[];
  /** Number of signatures required for approval (minimum 1, must not exceed signers.length) */
  threshold: number;
  /** Array of spending limits (minimum 1 required) */
  spendingLimits: SpendingLimitPolicy[];
  /** Optional start timestamp for the policy */
  startTimestamp?: number;
  /** Optional expiration arguments */
  expirationArgs?: PolicyExpirationArgs;
}

/**
 * Request to prepare a trading automation transaction
 *
 * This endpoint prepares transactions with policy validation for automated trading.
 */
export interface PrepareTradingAutomationRequest {
  /** Base64-encoded Solana transaction */
  transaction: string;
  /** Array of transaction signer public keys (optional, defaults to empty) */
  transactionSigners?: string[];
  /** Optional payer address for transaction fees */
  payerAddress?: string;
  /** Optional fee configuration */
  feeConfig?: FeeConfig;
}

/**
 * Query parameters for prepare trading automation endpoint
 */
export interface PrepareTradingAutomationQueryParams {
  /** Include simulation logs in response (defaults to false) */
  debug?: boolean;
}

/**
 * Response from preparing a trading automation transaction
 */
export interface PrepareTradingAutomationResponse extends BaseResponse<{
  /** Prepared transaction as base64 string */
  transaction: string;
  /** KMS payloads for signing */
  kmsPayloads: KmsPayload[];
  /** Optional simulation logs if debug=true */
  simulationLogs?: string[];
  /** Transaction signer addresses */
  transactionSigners: string[];
  /** Optional fee information */
  fee?: {
    amount: number;
    currency: string;
    breakdown: {
      computeFee: number;
      rentFee: number;
    };
  };
}> {}

// ============= Trade Smart Transaction Types =============

/**
 * Trading venue/platform for quote retrieval and execution
 */
export type TradeVenue = 'jupiter';

/**
 * Input token allowance configuration for trade policies
 */
export interface InputAllowance {
  /** Maximum amount that can be spent in smallest units */
  amount: string | number;
  /** Period type for the allowance (OneTime, Daily, Weekly, Monthly) */
  cadence: Period;
}

/**
 * Input token with optional spending allowance
 */
export interface InputWithAllowance {
  /** Token mint address */
  mint: string;
  /** Optional spending allowance for this input token */
  allowance?: InputAllowance;
}

/**
 * Trade rules configuration for smart transaction policies
 */
export interface TradeRules {
  /** Allowed input tokens with optional spending allowances (minimum 1 required) */
  allowedInputs: InputWithAllowance[];
  /** Allowed output token mint addresses (can be empty for any output) */
  allowedOutputs: string[];
  /** Maximum slippage in basis points (e.g., 100 = 1%, default 0) */
  maxSlippageBps: number;
  /** Optional platform fees in basis points (e.g., 10 = 0.1%) */
  feesBps?: number;
  /** Optional trading venue selection (defaults to Jupiter) */
  venue?: TradeVenue;
}

/**
 * Account policies for smart transactions
 *
 * Note: This is similar to TradePolicies but without spending limits,
 * as those are now part of TradeRules
 */
export interface AccountPoliciesSmartTransaction {
  /** Array of signers authorized for this policy (minimum 1 required) */
  signers: PolicySigner[];
  /** Number of signatures required for approval (minimum 1, must not exceed signers.length) */
  threshold: number;
  /** Optional start timestamp for the policy */
  startTimestamp?: number;
  /** Optional expiration arguments */
  expirationArgs?: PolicyExpirationArgs;
}

/**
 * Request to create a trade smart transaction policy
 *
 * @example
 * ```typescript
 * const request = {
 *   policies: {
 *     signers: [{
 *       address: '3ciascNndLTBrDQXvs8nzZgWAiaL33tGM6fx3zzM7Fxt',
 *       permissions: ['CAN_INITIATE', 'CAN_VOTE', 'CAN_EXECUTE']
 *     }],
 *     threshold: 1
 *   },
 *   rules: {
 *     allowedInputs: [{
 *       mint: 'So11111111111111111111111111111111111111112',
 *       allowance: {
 *         amount: '1000000000',
 *         cadence: 'Daily'
 *       }
 *     }],
 *     allowedOutputs: ['EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'],
 *     maxSlippageBps: 100,
 *     feesBps: 10
 *   }
 * };
 * const response = await client.createTradeSmartTransaction(accountAddress, request);
 * console.log(response.data.smartTransactionAddress);
 * ```
 */
export interface CreateTradeSmartTransactionRequest {
  /** Account policies for signers and threshold */
  policies: AccountPoliciesSmartTransaction;
  /** Trade rules defining allowed inputs, outputs, and constraints */
  rules: TradeRules;
  /** Optional transaction signers for creating the policy */
  transactionSigners?: string[];
}

/** Data returned from creating a trade smart transaction */
export interface CreateTradeSmartTransactionData {
  /** The policy address (smart transaction address) */
  smartTransactionAddress: string;
  /** Prepared transaction as base64 string */
  transaction: string;
  /** KMS payloads for signing */
  kmsPayloads: KmsPayload[];
  /** Transaction signer addresses */
  transactionSigners: string[];
}

/**
 * Response from creating a trade smart transaction
 */
export interface CreateTradeSmartTransactionResponse extends BaseResponse<CreateTradeSmartTransactionData> {}

/**
 * Request to get a standalone trade quote (no policy required)
 *
 * @example
 * ```typescript
 * const request = {
 *   venue: 'jupiter',
 *   fromMint: 'So11111111111111111111111111111111111111112',
 *   toMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
 *   amount: 1000000000,
 *   slippageBps: 50
 * };
 * const response = await client.getTradeQuote(request);
 * console.log(response.data.quote);
 * ```
 */
export interface GetTradeQuoteRequest {
  /** Trading venue to get quote from */
  venue: TradeVenue;
  /** Input token mint address */
  fromMint: string;
  /** Output token mint address */
  toMint: string;
  /** Amount of input token in smallest units (e.g., lamports for SOL) */
  amount: number | string;
  /** Maximum slippage tolerance in basis points (e.g., 50 = 0.5%) */
  slippageBps: number;
}

/** Data returned from trade quote request */
export interface GetTradeQuoteData {
  /** The trading venue that provided this quote */
  venue: TradeVenue;
  /** The raw quote response from the venue */
  quote: any;
}

/**
 * Response from getting a trade quote
 */
export interface GetTradeQuoteResponse extends BaseResponse<GetTradeQuoteData> {}

/**
 * Request to get a policy-aware trade quote
 *
 * @example
 * ```typescript
 * const request = {
 *   venue: 'jupiter',
 *   fromMint: 'So11111111111111111111111111111111111111112',
 *   toMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
 *   amount: 1000000000,
 *   slippageBps: 50
 * };
 * const response = await client.getSmartTransactionQuote(
 *   accountAddress,
 *   smartTransactionAddress,
 *   request
 * );
 * console.log(response.data.quote);
 * ```
 */
export interface TradeSmartTransactionQuoteRequest {
  /** Trading venue to get quote from */
  venue: TradeVenue;
  /** Input token mint address */
  fromMint: string;
  /** Output token mint address */
  toMint: string;
  /** Amount of input token in smallest units */
  amount: number | string;
  /** Maximum slippage tolerance in basis points */
  slippageBps: number;
}

/** Data returned from smart transaction quote */
export interface TradeSmartTransactionQuoteData {
  /** The trading venue that provided this quote */
  venue: TradeVenue;
  /** The raw quote response from the venue */
  quote: any;
}

/**
 * Response from getting a smart transaction quote
 */
export interface TradeSmartTransactionQuoteResponse extends BaseResponse<TradeSmartTransactionQuoteData> {}

/**
 * Trade details for preparing a trade transaction
 */
export interface TradeDetails {
  /** Input token mint address */
  input: string;
  /** Output token mint address */
  output: string;
  /** Amount in smallest units */
  amount: number | string;
  /** Slippage tolerance in basis points */
  slippageBps: number;
  /** Fee in basis points */
  feeBps: number;
}

/**
 * Quote wrapper for preparing a trade transaction
 */
export interface TradeQuote {
  /** Raw quote object from quote endpoint */
  quote: any;
}

/**
 * Trade arguments union type
 *
 * Either provide trade details OR a pre-fetched quote
 */
export type TradeArgs =
  | ({ type: 'arguments' } & TradeDetails)
  | { type: 'quote'; quote: any };

/**
 * Request to prepare a trade smart transaction
 *
 * @example Using trade details:
 * ```typescript
 * const request = {
 *   tradeArgs: {
 *     type: 'arguments',
 *     input: 'So11111111111111111111111111111111111111112',
 *     output: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
 *     amount: 1000000000,
 *     slippageBps: 50,
 *     feeBps: 10
 *   },
 *   transactionSigners: ['signer-pubkey']
 * };
 * const response = await client.prepareTradeSmartTransaction(
 *   accountAddress,
 *   smartTransactionAddress,
 *   request,
 *   { debug: true }
 * );
 * ```
 *
 * @example Using pre-fetched quote:
 * ```typescript
 * const quoteResponse = await client.getSmartTransactionQuote(...);
 * const request = {
 *   tradeArgs: {
 *     type: 'quote',
 *     quote: quoteResponse.data.quote
 *   }
 * };
 * const response = await client.prepareTradeSmartTransaction(...);
 * ```
 */
export interface PrepareTradeSmartTransactionRequest {
  /** Trade arguments (either details or quote) */
  tradeArgs: TradeArgs;
  /** Optional transaction signers */
  transactionSigners?: string[];
  /** Optional payer address for transaction fees */
  payerAddress?: string;
  /** Optional fee configuration */
  feeConfig?: FeeConfig;
}

/**
 * Query parameters for prepare trade smart transaction
 */
export interface PrepareTradeSmartTransactionQueryParams {
  /** Include simulation logs in response (defaults to false) */
  debug?: boolean;
}

/** Data returned from preparing a trade smart transaction */
export interface PrepareTradeSmartTransactionData {
  /** Prepared transaction as base64 string */
  transaction: string;
  /** KMS payloads for signing */
  kmsPayloads: KmsPayload[];
  /** Optional simulation logs if debug=true */
  simulationLogs?: string[];
  /** Transaction signer addresses */
  transactionSigners: string[];
  /** Optional fee information */
  fee?: {
    amount: number;
    currency: string;
    breakdown: {
      computeFee: number;
      rentFee: number;
    };
  };
}

/**
 * Response from preparing a trade smart transaction
 */
export interface PrepareTradeSmartTransactionResponse extends BaseResponse<PrepareTradeSmartTransactionData> {}

/**
 * Allowed input token configuration from a trade policy
 */
export interface AllowedInput {
  /** Token mint address */
  mint: string;
  /** Allowance information if set */
  allowance?: {
    /** Amount allowed */
    amount: string;
    /** Cadence/period */
    cadence: Period;
    /** Remaining amount available */
    remaining: string;
  };
}

/**
 * Tradable assets configuration from a trade policy
 */
export interface TradableAssets {
  /** Array of allowed input tokens with allowance info */
  allowedInputs: AllowedInput[];
  /** Array of allowed output token mint addresses */
  allowedOutputs: string[];
}

/**
 * Policy status enum
 */
export type PolicyStatus = 'active' | 'expired' | 'cancelled';

/** Data returned from getting a trade smart transaction */
export interface GetTradeSmartTransactionData {
  /** Policy address */
  policyAddress: string;
  /** Authorized signers as JSON */
  signers: any;
  /** Required number of signatures */
  threshold: number;
  /** Tradable assets configuration */
  tradableAssets: TradableAssets;
  /** Optional maximum slippage in basis points */
  maxSlippageBps?: number;
  /** Policy status */
  status: PolicyStatus;
}

/**
 * Response from getting a trade smart transaction
 *
 * @example
 * ```typescript
 * const response = await client.getTradeSmartTransaction(
 *   accountAddress,
 *   smartTransactionAddress
 * );
 * console.log(response.data.tradableAssets);
 * console.log(response.data.status);
 * ```
 */
export interface GetTradeSmartTransactionResponse extends BaseResponse<GetTradeSmartTransactionData> {}

/**
 * Remove Passkey Request
 */
export interface RemovePasskeyRequest {
  transactionSigners?: string[]; // Option<Vec<Pubkey>> mapped to string array
}

/**
 * Remove Passkey Query Params
 */
export interface RemovePasskeyQueryParams {
  admin?: boolean;              // Optional admin flag with default false
}

/**
 * Remove Passkey Response
 */
export interface RemovePasskeyResponse extends BaseResponse<{
  transaction: string;
  removedPasskey: PasskeyInfo; // Reuse existing PasskeyInfo type
  threshold: number;            // u8 mapped to number
  status: string;
  expiresAt: string;           // DateTime<Utc> mapped to ISO string
}> {}

// Transfer State types
export type TransferState = 
  | 'awaiting_funds'
  | 'in_review' 
  | 'funds_received'
  | 'payment_submitted'
  | 'payment_processed'
  | 'canceled'
  | 'error'
  | 'undeliverable'
  | 'returned'
  | 'refunded';

// Comprehensive Transfer types for get_transfers endpoint
export type ConfirmationStatus = 'pending' | 'confirmed';
export type TransferDirection = 'inflow' | 'outflow';

// Receipt types
export interface Receipt {
  id: string;
  object: string;
  createdAt: string; // ISO string from DateTime<Utc>
}

// Bridge Transfer types
export interface BridgeTransferSource {
  paymentRail: PaymentRail;
  currency: BridgeCurrency;
  externalAccountId?: string;
  bridgeWalletId?: string;
  fromAddress?: string;
  omad?: string;
  imad?: string;
  bankBeneficiaryName?: string;
}

// Bridge Transfer Destination
export interface BridgeTransferDestination {
  currency: BridgeCurrency;
  paymentRail: PaymentRail;
  externalAccountId?: string;
  bridgeWalletId?: string;
  fromAddress?: string;
  wireMessage?: string;
  sepaReference?: string;
  swiftReference?: string;
}

// Bridge Transfer Source Deposit Instructions
export interface BridgeTransferSourceDepositInstructions {
  amount?: string; // Decimal from Rust
  currency: BridgeCurrency;
  depositMessage?: string;
  paymentRail: PaymentRail;
  fromAddress?: string;
  toAddress?: string;
  bankName?: string;
  bankAddress?: string;
  bankCity?: string;
  bankCountry?: string;
  bankPostalCode?: string;
  iban?: string;
  swiftBic?: string;
  beneficiaryName?: string;
  beneficiaryAddress?: string;
  beneficiaryCity?: string;
  beneficiaryCountry?: string;
  beneficiaryPostalCode?: string;
  routingNumber?: string;
  accountNumber?: string;
  accountType?: string;
}

/**
 * Bridge transfer data for fiat-to-crypto or crypto-to-fiat transfers
 *
 * Represents transfers processed through Bridge's payment rails, including
 * ACH, SEPA, wire transfers, and other traditional banking methods.
 */
export interface BridgeTransfer {
  id: string;
  state: TransferState;
  onBehalfOf: string;
  sourceDepositInstructions?: BridgeTransferSourceDepositInstructions;
  amount: string; // Decimal from Rust
  clientReferenceId?: string;
  currency?: BridgeCurrency;
  developerFee: string; // Decimal from Rust
  source: BridgeTransferSource;
  destination: BridgeTransferDestination;
  receipt: Receipt;
  blockchainMemo?: string;
  createdAt: string; // ISO string from DateTime<Utc>
  updatedAt: string; // ISO string from DateTime<Utc>
}

/** SPL token transfer data for on-chain Solana transfers (supports SPL Token and Token-2022) */
export interface SplTransfer {
  id: string;
  gridUserId: string;
  mainAccountAddress: string;
  mint: string;
  isToken2022: boolean;
  signature: string;
  confirmationStatus: ConfirmationStatus;
  fromAddress: string;
  toAddress: string;
  amount: string;
  uiAmount: string;
  decimals: number;
  confirmedAt?: string;
  createdAt: string;
  updatedAt: string;
  direction?: TransferDirection;
  environment?: GridEnvironment;
  instructionIndex?: number;
}

/**
 * Get transfers response
 *
 * @see {@link BridgeTransfer} for fiat-to-crypto transfers
 * @see {@link SplTransfer} for on-chain transfers
 */
export interface TransferResponse extends BaseResponse<(BridgeTransfer | SplTransfer)[]> {
  data: (BridgeTransfer | SplTransfer)[];
  /** Next page cursor */
  nextCursor?: string;
  /** More results available */
  hasMore?: boolean;
}

// Options for get_transfers endpoint
export interface GetTransfersOptions {
  paymentRail?: PaymentRail; // renamed from 'type' to avoid TypeScript keyword
  status?: TransferState;
  currency?: BridgeCurrency;
  startDate?: string;
  endDate?: string;
  txHash?: string;
  limit?: number;
  cursor?: string;
}


// Currency types
export type BridgeCurrency = 'usd' | 'eur';

/** Request virtual account */
export interface RequestVirtualAccountRequest {
  currency: BridgeCurrency;
}

/** @hidden */
export interface SourceDepositInstructions {
  currency: string;
  bankBeneficiaryName: string;
  bankName: string;
  bankAddress: string;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  paymentRails: string[];
}

/** @hidden */
export interface VirtualAccountDestination {
  currency: string;
  paymentRail: string;
  address: string;
}

export interface VirtualAccount {
  id: string;
  customerId: string;
  sourceDepositInstructions: SourceDepositInstructions;
  destination: VirtualAccountDestination;
  status: string;
  developerFeePercent: string;
}

// Virtual Account types
export interface VirtualAccountResponse extends BaseResponse<VirtualAccount> {}
export interface VirtualAccountsResponse extends BaseResponse<VirtualAccount[]> {}

/** Get virtual accounts query params */
export interface GetVirtualAccountsQueryParams {
  sourceCurrency?: BridgeCurrency;
  destinationCurrency?: BridgeCurrency;
}

/** Standing order frequency */
export type StandingOrderFrequency = 'weekly' | 'monthly';

/** Standing order status */
export type StandingOrderStatus = 'active' | 'inactive' | 'cancelled' | 'completed';

/** Payment rail type */
export type PaymentRail = 'ach_push' | 'ach_pull' | 'sepa' | 'faster_payments' | 'wire' | 'solana' | 'smart_account';

/** Grid account details */
export interface GridAccountDetails {
  account: string;
  currency: string;
  transactionSigners?: string[];
}

/** Solana address details */
export interface SolanaDetails {
  address: string;
  currency: string;
}

/**
 * New External Account Details
 */
export interface NewExternalAccountDetails {
  paymentRail: PaymentRail;
  currency: string;
  details: any; // CreateExternalAccountRequest - would need proper typing
}

/**
 * Existing External Account Details
 */
export interface ExistingExternalAccountDetails {
  paymentRail: PaymentRail;
  currency: string;
  externalAccountId: string;
}

/**
 * Details
 */
export type Details =
  | GridAccountDetails           // has 'account' field
  | SolanaDetails                // has 'address' field
  | NewExternalAccountDetails    // has 'details' field
  | ExistingExternalAccountDetails; // has 'external_account_id' field

/**
 * Create Standing Order Request
 */
export interface CreateStandingOrderRequest {
  amount: string;
  source: Details;
  destination: Details;
  frequency: StandingOrderFrequency;
  startDate: string; // ISO date string
  endDate?: string; // Optional ISO date string
}

/** Create standing order response */
export interface CreateStandingOrderResponse extends BaseResponse<{
    /** Standing order identifier */
    id: string;
    /** Amount as string */
    amount: string;
    /** Human-readable amount */
    uiAmount: string;
    /** Currency type */
    currency: BridgeCurrency;
    /** Payment rail used */
    paymentRail: PaymentRail;
    /** Source account details */
    source: Details;
    /** Destination account details */
    destination: Details;
    /** Recurrence frequency */
    frequency: StandingOrderFrequency;
    /** Start date (ISO string) */
    startDate: string;
    /** End date (ISO string, optional) */
    endDate?: string;
    /** Current status */
    status: StandingOrderStatus;
    /** Serialized transaction */
    transaction: string;
    kmsPayloads: KmsPayload[];
    transactionSigners: string[];
    validUntil: string;
    createdAt: string;
    transactionPayload: TransactionPayload;
}> {}

/**
 * Get Standing Orders Query Params
 */
export interface GetStandingOrdersQueryParams {
  status?: StandingOrderStatus;
  currency?: string;  // BridgeCurrency as string
  startDate?: string;
  endDate?: string;
  txHash?: string;
  limit?: number;
  cursor?: string;
}

// Get Standing Orders types
export interface StandingOrder {
  id: string;
  amount: string;
  currency: string;
  period: StandingOrderFrequency;
  destinations: string[];
  status: StandingOrderStatus;
  remainingAmount: string;
  lastExecutionDate?: string;  // ISO date string
  nextExecutionDate: string;    // ISO date string
  createdAt: string;             // ISO date string
}

export interface StandingOrderResponse extends BaseResponse<StandingOrder> {}
export interface StandingOrdersResponse extends BaseResponse<StandingOrder[]> {}

/**
 * Available signer provider types for authentication and transaction signing.
 * @typedef {'privy' | 'turnkey' | 'solana' | 'passkey' | 'pending' | 'external'} SignerProvider
 */
export type SignerProvider = 'privy' | 'turnkey' | 'solana' | 'passkey' | 'pending' | 'external';

// Grid Client Signer
export interface GridClientSigner {
  address?: string;               // Optional - Solana/passkey signers have address
  email?: string;                 // Optional - Privy/Turnkey signers may have email
  sessionKey?: any;
  type: 'primary' | 'backup';     // Signer priority/role
  provider?: SignerProvider;      // Optional - The provider type for this signer
}

// Grid Client Context types
export interface GridClientUserContext {
  // data: {
    address?: string;          // The single smart account address (once created)
    gridUserId?: string;           // Grid platform user ID
    signers?: GridClientSigner[];  // Array of all signers who can authorize transactions
    session?: Session;
    email?: string;
    otpId?: string;
    provider?: SignerProvider | string;
  // }
}

// @hidden
export interface EncryptedAuthorizationKey {
  encryption_type: string;
  encapsulated_key: string;
  ciphertext: string;
}

/** @hidden */
export type WalletChainType =
  | 'Solana'
  | 'Ethereum'
  | 'Cosmos'
  | 'Stellar'
  | 'Sui'
  | 'Tron'
  | 'bitcoin-segwit';

/** @hidden */
export interface AdditionalSigner {
  signerId: string;
  overridePolicyIds?: string[]; // Optional Vec<String>
}

// Wallet created during registration
export interface Wallet {
  id: string;
  address: string;
  publicKey?: string; // Option<String>
  createdAt: number; // u64
  chainType: WalletChainType;
  policyIds: string[];
  ownerId?: string; // Option<String>
  additionalSigners: AdditionalSigner[];
  exportedAt?: number; // Option<u64>
  importedAt?: number; // Option<u64>
}

// Base session structure with authorization keys
export interface Session {
  encryptedAuthorizationKey?: EncryptedAuthorizationKey; // Option<EncryptedAuthorizationKey>
  authorizationKey?: string; // Option<String>
  expiresAt: number; // u64
  wallets: Wallet[];
}

// Refresh Session types
/**
 * Privy-specific session data for refresh operations
 */
export interface PrivyRefreshSession {
  /** Privy user identifier */
  user_id: string;
  /** Session data containing authorization keys and wallet information */
  session: Session;
  /** JWT token for Privy authentication */
  token: string;
  /** Privy platform access token */
  privy_access_token: string;
  /** Refresh token for obtaining new access tokens */
  refresh_token: string;
}

/**
 * Turnkey session data for refresh
 *
 * Note: Turnkey refresh not yet implemented in API
 */
export interface TurnkeyRefreshSession {
  userId: string;
  api_key_id: string;
  /** Encrypted credential bundle */
  credential_bundle: string;
}

/** Provider session data (discriminated union by provider name) */
export type ProviderSessionData =
  | { Privy: PrivyRefreshSession }
  | { Turnkey: TurnkeyRefreshSession };

/** Session provider entry with provider name and session data */
export interface SessionProvider {
  /** Provider name (e.g., 'privy', 'turnkey', 'Passkey') */
  provider: string;
  /** Provider-specific session data */
  session?: PrivyRefreshSession | TurnkeyRefreshSession | any;
  /** Session key (Passkey provider only) */
  sessionKey?: { key: any; expiration: number };
  /** Passkey account address (Passkey provider only) */
  passkeyAccount?: string;
  /** Public key in base58 (Passkey provider only) */
  pubkey?: string;
  /** Relying party ID/domain (Passkey provider only) */
  relyingPartyId?: string;
  /** Passkey role */
  role?: string;
  /** Passkey permissions */
  permissions?: string[];
}

/** Array of session providers from authentication endpoints */
export type AuthenticationData = SessionProvider[];

/** KMS provider configuration for session refresh */
export interface RefreshSessionKmsProvider {
  /** MPC provider type */
  provider: string;
  /** Provider-specific session data */
  session: ProviderSessionData;
}

/**
 * Refresh session request (supports cookie-based and body-based flows)
 *
 * - Cookie-based (recommended): Omit kmsPayload, uses HTTP-only cookies
 * - Body-based (legacy): Include kmsPayload with tokens
 */
export interface RefreshSessionRequest {
  /** KMS provider config (optional with HTTP-only cookies) */
  kmsPayload?: RefreshSessionKmsProvider;
  /** HPKE public key for response encryption */
  encryptionPublicKey: string;
}

/** Refresh session response */
export interface RefreshSessionResponse extends BaseResponse<{
  /** Updated KMS provider and session data */
  kmsPayload: RefreshSessionKmsProvider;
}> {}

/** @hidden */
export interface ApiRefreshSessionResponse extends BaseResponse<{
  kms_payload: RefreshSessionKmsProvider;
}> {}

/** @hidden */
export interface ClientContext {
  environment: GridEnvironment;
  config: GridApiConfig;
  solanaRpcUrl?: string;
}

/** Grid client context */
export interface GridClientContext {
  client: ClientContext;
  user: GridClientUserContext;
}

/** Complete auth and create account response */
export interface CompleteAuthAndCreateAccountResponse extends BaseResponse<{
  address: string;
  status: string;
  policies: AccountPolicies;
  gridUserId: string;
  authentication: AuthenticationData;
}> {}

/** Account state for reactive UI updates */
export interface AccountState {
  /** Smart account address (if connected) */
  accountAddress?: string;
  /** Grid user ID (if connected) */
  gridUserId?: string;
  /** Whether account is connected */
  isConnected: boolean;
}
